import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import { NumericTextField } from '@/components';
import type { PackagingInputs as PackagingInputsType } from '@/stores/useInputStore';
import { useUIStore } from '@/stores/useUIStore';
import type { ParamRange } from '@/api/trainingDataApi';

interface PackagingInputsProps {
  values: PackagingInputsType;
  onChange: <K extends keyof PackagingInputsType>(key: K, value: PackagingInputsType[K]) => void;
  /** Whether all packaging fields are disabled (e.g. BYD mode). */
  disabled?: boolean;
  /** Validation errors keyed by field name. */
  errors?: Partial<Record<keyof PackagingInputsType, string>>;
  /** Dynamic min/max ranges from training data. */
  ranges?: Partial<Record<keyof PackagingInputsType, ParamRange>>;
}

/** Configuration for each packaging field. */
const FIELDS: Array<{
  key: keyof PackagingInputsType;
  label: string;
  unit: string;
}> = [
  { key: 'coreLength', label: 'Core Length', unit: 'mm' },
  { key: 'outerSleeveLength', label: 'Sleeve Length', unit: 'mm' },
  { key: 'controlArmId', label: 'Control Arm ID', unit: 'mm' },
  { key: 'coreId', label: 'Core ID', unit: 'mm' },
];

/** Fields that should be clamped to min/max on blur. */
const CLAMP_FIELDS: Set<keyof PackagingInputsType> = new Set(['coreLength', 'outerSleeveLength']);

/**
 * PackagingInputs — 4-column grid of core geometry input fields.
 *
 * Renders Core Length, Sleeve Length, Control Arm ID, and Core ID
 * as compact numeric text fields.
 *
 * For Core Length and Sleeve Length:
 * - Out-of-range values show the valid range as a helper message while typing.
 * - On blur (leaving the field), values are auto-corrected:
 *   below min → set to min, above max → set to max.
 * - User can always re-edit after auto-correction.
 */
const PackagingInputs: React.FC<PackagingInputsProps> = ({
  values,
  onChange,
  disabled = false,
  errors = {},
  ranges = {},
}) => {
  /** On blur, clamp coreLength and outerSleeveLength to the valid range and show dialog. */
  const handleBlur = useCallback(
    (key: keyof PackagingInputsType, currentValue: number | null) => {
      if (currentValue === null || !CLAMP_FIELDS.has(key)) return;
      const range = ranges[key];
      if (!range) return;

      const fieldLabel = key === 'coreLength' ? 'core length' : 'sleeve length';

      if (currentValue < range.min) {
        onChange(key, range.min);
        useUIStore.getState().openLengthLimitDialog(fieldLabel, range.min, range.max);
      } else if (currentValue > range.max) {
        onChange(key, range.max);
        useUIStore.getState().openLengthLimitDialog(fieldLabel, range.min, range.max);
      }
    },
    [onChange, ranges],
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 1,
      }}
    >
      {FIELDS.map(({ key, label, unit }) => {
        const range = ranges[key];
        return (
          <NumericTextField
            key={key}
            label={`${label} [${unit}]`}
            value={values[key]}
            onChange={(v) => onChange(key, v)}
            onBlur={() => handleBlur(key, values[key])}
            disabled={disabled}
            error={!!errors[key]}
            helperText={errors[key]}
            min={range?.min}
            max={range?.max}
            size="small"
            fullWidth
          />
        );
      })}
    </Box>
  );
};

export default React.memo(PackagingInputs);
